import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { createChart, LineStyle, CrosshairMode, CandlestickSeries } from 'lightweight-charts';
import { useLanguage } from '../../context/LanguageContext';
import { TrendingUp, TrendingDown, Activity, Zap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURED = [
  { sym: 'BTC/USD',  display: 'BTC/USD',  name: { ar: 'بيتكوين',  en: 'Bitcoin'  }, precision: 2, minMove: 0.01 },
  { sym: 'ETH/USD',  display: 'ETH/USD',  name: { ar: 'إيثيريوم', en: 'Ethereum' }, precision: 2, minMove: 0.01 },
  { sym: 'EUR/USD',  display: 'EUR/USD',  name: { ar: 'يورو/دولار', en: 'EUR/USD' }, precision: 5, minMove: 0.00001 },
  { sym: 'XAU/USD',  display: 'XAU/USD',  name: { ar: 'الذهب', en: 'Gold' }, precision: 2, minMove: 0.01 },
  { sym: 'GBP/USD',  display: 'GBP/USD',  name: { ar: 'جنيه/دولار', en: 'GBP/USD' }, precision: 5, minMove: 0.00001 },
  { sym: 'SOL/USD',  display: 'SOL/USD',  name: { ar: 'سولانا', en: 'Solana' }, precision: 3, minMove: 0.001 },
];

const INTERVALS = [
  { id: '5M',  label: '5m' },
  { id: '15M', label: '15m' },
  { id: '1H',  label: '1h', default: true },
  { id: '4H',  label: '4h' },
  { id: '1D',  label: '1d' },
];

const LiveCharts = () => {
  const { isRTL } = useLanguage();
  const [active, setActive] = useState(FEATURED[0]);
  const [interval, setIntervalKey] = useState('1H');
  const [quote, setQuote] = useState(null); // { bid, ask, change_24h }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const lastCandleRef = useRef(null); // last drawn candle for live in-progress updates

  // Build chart once
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      autoSize: true,
      localization: { locale: 'en-US' },
      layout: { background: { color: 'transparent' }, textColor: '#6E7682', fontFamily: 'Inter, system-ui, sans-serif' },
      grid: { vertLines: { color: 'rgba(20,15,10,0.04)' }, horzLines: { color: 'rgba(20,15,10,0.04)' } },
      crosshair: { mode: CrosshairMode.Magnet },
      rightPriceScale: { borderColor: 'rgba(20,15,10,0.06)' },
      timeScale: { borderColor: 'rgba(20,15,10,0.06)', timeVisible: true, secondsVisible: false },
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#16A765', downColor: '#DC2626',
      borderUpColor: '#16A765', borderDownColor: '#DC2626',
      wickUpColor: '#16A765', wickDownColor: '#DC2626',
      priceLineStyle: LineStyle.Dashed,
    });
    chartRef.current = chart;
    seriesRef.current = series;
    return () => { chart.remove(); chartRef.current = null; seriesRef.current = null; };
  }, []);

  // Apply per-asset price precision so the right-axis labels stay correct for FX vs crypto
  useEffect(() => {
    if (!seriesRef.current) return;
    seriesRef.current.applyOptions({
      priceFormat: { type: 'price', precision: active.precision ?? 2, minMove: active.minMove ?? 0.01 },
    });
  }, [active]);

  // Track all featured quotes for the side ticker panel
  const [allQuotes, setAllQuotes] = useState({}); // { 'BTC/USD': { bid, change, prev } }

  const loadHistory = useCallback(async (symbol, intv) => {
    setLoading(true); setError('');
    try {
      const encoded = symbol.replace('/', '');
      const { data } = await axios.get(`${API}/api/assets/history/${encoded}/${intv}?limit=200`);
      const candles = (data?.candles || []).map(c => ({
        time: c.time, open: c.open, high: c.high, low: c.low, close: c.close,
      }));
      seriesRef.current?.setData(candles);
      lastCandleRef.current = candles.length ? { ...candles[candles.length - 1] } : null;
      chartRef.current?.timeScale().fitContent();
    } catch (e) {
      setError('Failed to load chart');
    } finally { setLoading(false); }
  }, []);

  const activeRef = useRef(active);
  useEffect(() => { activeRef.current = active; }, [active]);

  // Apply a price update (from WS or REST) to both the header quote and the latest candle
  const applyTick = useCallback((symbol, q) => {
    // Update the cross-asset Market-Pulse panel
    setAllQuotes(prev => {
      const next = { ...prev };
      const f = FEATURED.find(x => x.sym === symbol);
      if (!f) return prev;
      const previous = next[symbol]?.bid;
      next[symbol] = {
        bid: q.bid ?? q.price,
        change: q.change_24h ?? next[symbol]?.change ?? 0,
        tick: previous && previous !== (q.bid ?? q.price) ? ((q.bid ?? q.price) > previous ? 'up' : 'down') : next[symbol]?.tick || null,
        tickAt: previous && previous !== (q.bid ?? q.price) ? Date.now() : next[symbol]?.tickAt || 0,
      };
      return next;
    });

    if (symbol !== activeRef.current.sym) return;

    setQuote(prev => ({
      bid: q.bid ?? q.price,
      ask: q.ask ?? (q.bid ?? q.price) * 1.0001,
      change_24h: q.change_24h ?? prev?.change_24h ?? 0,
      high: q.high_24h ?? prev?.high,
      low: q.low_24h ?? prev?.low,
    }));

    // Live-update the latest candle on the chart
    if (seriesRef.current && lastCandleRef.current) {
      const cur = lastCandleRef.current;
      const px = q.bid ?? q.price;
      if (px) {
        const updated = {
          time: cur.time,
          open: cur.open,
          close: px,
          high: Math.max(cur.high, px),
          low: Math.min(cur.low, px),
        };
        lastCandleRef.current = updated;
        try { seriesRef.current.update(updated); } catch { /* noop */ }
      }
    }
  }, []);

  // WebSocket subscription to backend's live price stream
  useEffect(() => {
    const wsUrl = (API || '').replace(/^http/i, 'ws') + '/api/ws/prices';
    let ws;
    let stopped = false;
    let backoff = 1000;

    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);
      } catch { return; }

      ws.onopen = () => {
        backoff = 1000;
        ws.send(JSON.stringify({ type: 'subscribe', symbols: FEATURED.map(f => f.sym) }));
      };
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if ((m.type === 'snapshot' || m.type === 'tick') && m.prices) {
            for (const sym of Object.keys(m.prices)) {
              if (FEATURED.find(f => f.sym === sym)) applyTick(sym, m.prices[sym]);
            }
          }
        } catch { /* noop */ }
      };
      ws.onclose = () => {
        if (stopped) return;
        setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, 15000);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };

    connect();
    return () => { stopped = true; try { ws?.close(); } catch {} };
  }, [applyTick]);

  useEffect(() => {
    // Clear stale quote so the price bar doesn't briefly show the previous asset's number
    setQuote(null);
    lastCandleRef.current = null;
    loadHistory(active.sym, interval);
  }, [active, interval, loadHistory]);

  const t = (ar, en) => (isRTL ? ar : en);
  const isUp = (quote?.change_24h || 0) >= 0;

  // Animated heading variants — staggered word reveal
  const headingWords = isRTL
    ? ['تداول', 'الأسواق', 'العالمية', 'في', 'الوقت', 'الحقيقي.']
    : ['Trade', 'global', 'markets', 'in', 'real', 'time.'];

  return (
    <section className="py-20 bg-[#FAF7F2] relative overflow-hidden" data-testid="home-live-charts">
      {/* Decorative grid + glow */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.18]" style={{
        backgroundImage: 'linear-gradient(rgba(20,15,10,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(20,15,10,0.05) 1px, transparent 1px)',
        backgroundSize: '48px 48px',
        maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
      }} />
      <div className="absolute top-0 start-1/2 -translate-x-1/2 w-[60rem] h-[20rem] bg-[#D85308]/8 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-[1320px] mx-auto px-6 relative">
        <div className={`flex items-end justify-between mb-10 flex-wrap gap-4 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#D85308]/15 mb-4"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#D85308] opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D85308]" />
              </span>
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#D85308]">
                {t('الأسواق المباشرة', 'Live Markets')}
              </span>
            </motion.div>
            <h2 className="text-[clamp(2rem,4vw,2.8rem)] font-bold tracking-tight text-ink-900 leading-[1.1] max-w-[640px]">
              {headingWords.map((w, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
                  className={`inline-block ${i === headingWords.length - 1 ? 'text-[#D85308]' : ''}`}
                  style={{ marginInlineEnd: '0.25em' }}
                >
                  {w}
                </motion.span>
              ))}
            </h2>
          </div>
          <div className="flex gap-1.5 p-1 bg-white rounded-lg border border-ink-100 shadow-sm" data-testid="live-charts-intervals">
            {INTERVALS.map(i => (
              <button key={i.id} onClick={() => setIntervalKey(i.id)}
                className={`h-9 px-3 rounded-md text-[12.5px] font-semibold transition ${interval === i.id ? 'bg-ink-900 text-white' : 'text-ink-500 hover:text-ink-900'}`}
                data-testid={`live-charts-interval-${i.id}`}>
                {i.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Live Pulse panel — animated price ticker */}
          <div className="col-span-12 md:col-span-3 bg-gradient-to-br from-[#0B0F14] via-[#111A26] to-[#050A12] text-white rounded-2xl p-4 relative overflow-hidden" data-testid="live-charts-pulse">
            <div className="absolute -top-12 -end-12 w-40 h-40 rounded-full bg-[#D85308]/15 blur-3xl" />
            <div className={`flex items-center justify-between mb-4 relative ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#D85308]" />
                <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-white/70">{t('نبض السوق', 'Market Pulse')}</span>
              </div>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 text-[9.5px] font-bold uppercase tracking-wider">
                <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
                {t('مباشر', 'Live')}
              </span>
            </div>
            <div className="space-y-1 relative">
              {FEATURED.map(a => {
                const q = allQuotes[a.sym];
                const isActive = active.sym === a.sym;
                const tick = q?.tick;
                const tickFresh = q?.tickAt && (Date.now() - q.tickAt) < 1500;
                const tickColor = tickFresh ? (tick === 'up' ? 'text-emerald-400' : tick === 'down' ? 'text-red-400' : 'text-white') : 'text-white';
                return (
                  <button key={a.sym} onClick={() => setActive(a)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-[12.5px] transition ${isActive ? 'bg-white/8 border border-[#D85308]/30' : 'hover:bg-white/[0.04] border border-transparent'}`}
                    data-testid={`live-charts-asset-${a.sym.replace('/', '')}`}>
                    <div className={`${isRTL ? 'text-right' : 'text-left'} min-w-0`}>
                      <div className="font-semibold text-white truncate flex items-center gap-1.5">
                        {a.display}
                        {isActive && <Zap className="w-3 h-3 text-[#D85308]" />}
                      </div>
                      <div className="text-[10px] text-white/45 truncate">{isRTL ? a.name.ar : a.name.en}</div>
                    </div>
                    <div className={`${isRTL ? 'text-left' : 'text-right'} shrink-0`}>
                      <motion.div
                        key={q?.bid + '_' + (q?.tickAt || 0)}
                        initial={tickFresh ? { scale: 1.06 } : false}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.4 }}
                        className={`font-semibold tabular-nums text-[12.5px] transition-colors ${tickColor}`}
                      >
                        {q?.bid?.toLocaleString(undefined, { maximumFractionDigits: 6 }) || '—'}
                      </motion.div>
                      <div className={`text-[10px] font-semibold tabular-nums ${q?.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {q?.change >= 0 ? '+' : ''}{(q?.change || 0).toFixed(2)}%
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Chart panel */}
          <div className="col-span-12 md:col-span-9 bg-white rounded-2xl border border-ink-100 p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3" data-testid="live-charts-header">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[18px] font-bold text-ink-900 font-geist-mono tracking-tight">{active.display}</span>
                  <span className="text-[12px] text-ink-500">{isRTL ? active.name.ar : active.name.en}</span>
                </div>
                {quote && (
                  <div className={`flex items-center gap-3 mt-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <motion.span
                      key={quote.bid}
                      initial={{ scale: 1.04 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.35 }}
                      className="text-[26px] font-bold tabular-nums text-ink-900"
                    >
                      {quote.bid?.toLocaleString(undefined, { maximumFractionDigits: 6 })}
                    </motion.span>
                    <span className={`inline-flex items-center gap-1 text-[12.5px] font-semibold px-2 py-1 rounded-md ${isUp ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                      {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {isUp ? '+' : ''}{(quote.change_24h || 0).toFixed(2)}%
                    </span>
                  </div>
                )}
              </div>
              {quote && (
                <div className="flex gap-5 text-[11.5px]" data-testid="live-charts-stats">
                  <Stat label={t('عرض', 'Bid')} value={quote.bid?.toLocaleString(undefined, { maximumFractionDigits: 6 })} />
                  <Stat label={t('طلب', 'Ask')} value={quote.ask?.toLocaleString(undefined, { maximumFractionDigits: 6 })} />
                  <Stat label={t('قمة', '24h H')} value={quote.high?.toLocaleString(undefined, { maximumFractionDigits: 6 })} />
                  <Stat label={t('قاع', '24h L')} value={quote.low?.toLocaleString(undefined, { maximumFractionDigits: 6 })} />
                </div>
              )}
            </div>
            <div ref={containerRef} className="w-full h-[420px] relative" data-testid="live-charts-canvas">
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm rounded-xl z-10">
                  <div className="w-7 h-7 rounded-full border-2 border-[#D85308]/30 border-t-[#D85308] animate-spin" />
                </div>
              )}
              {error && !loading && (
                <div className="absolute inset-0 flex items-center justify-center text-[13px] text-red-600">{error}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.06em] font-semibold text-ink-400">{label}</div>
    <div className="text-[13px] font-semibold text-ink-900 tabular-nums">{value}</div>
  </div>
);

export default LiveCharts;
